import React, { useState, useEffect } from 'react';
import {
    Typography,
    TextField,
    Box,
    Button,
} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import { makeStyles } from '@mui/styles';
import UsersCombo from '../components/UsersCombo';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';

const useStyles = makeStyles(theme => ({
    inputField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#F9FAFB',
            '&:hover': {
                backgroundColor: '#F3F4F6',
            },
            '&.Mui-focused': {
                backgroundColor: '#FFFFFF',
            },
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5E7EB',
        },
        '& .MuiInputLabel-root': {
            color: '#6B7280',
        },
    },
    inputRtl: {
        "& label": {
            right: 20,
            left: "auto"
        },
        "& legend": {
            textAlign: "right"
        }
    }
}));

export default function AddMoney(props) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const {
        addToWallet
    } = api;
    const settings = useSelector(state => state.settingsdata.settings);
    const dispatch = useDispatch();
    const classes = useStyles();
    const userdata = useSelector(state => state.usersdata);
    const [users, setUsers] = useState(null);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
    const [userCombo, setUserCombo] = useState(null);
    const [amount, setAmount] = useState(0);

    useEffect(() => {
        if (userdata.users) {
            let arr = [];
            for (let i = 0; i < userdata.users.length; i++) {
                let user = userdata.users[i];
                arr.push({
                    'firstName': user.firstName,
                    'lastName': user.lastName,
                    'mobile': user.mobile,
                    'email': user.email,
                    'uid': user.id,
                    'desc': user.firstName + ' ' + user.lastName + ' (' + (settings.AllowCriticalEditsAdmin ? user.mobile : "Hidden") + ') ' + (settings.AllowCriticalEditsAdmin ? user.email : "Hidden"),
                    'pushToken': user.pushToken
                });
            }
            setUsers(arr);
        }
    }, [userdata.users, settings.AllowCriticalEditsAdmin]);

    const handleAddBalance = () => {
        if (userCombo && userCombo.uid && amount > 0) {
            dispatch(addToWallet(userCombo.uid, amount));
            setCommonAlert({ open: true, msg: t('success') });
        } else {
            setCommonAlert({ open: true, msg: t('no_details_error') });
        }
    }

    const handleCommonAlertClose = (e) => {
        e.preventDefault();
        setCommonAlert({ open: false, msg: '' })
    };

    return (
        <Box sx={{ maxWidth: 600, mx: 'auto' }} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            {/* Page Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
                    {t('add_to_wallet')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                    {t('add_money_description') || 'Add funds to a user\'s wallet'}
                </Typography>
            </Box>

            {/* Card */}
            <Box
                sx={{
                    bgcolor: '#FFFFFF',
                    borderRadius: 3,
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                }}
            >
                {/* Card Header */}
                <Box
                    sx={{
                        p: 2.5,
                        borderBottom: '1px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: '#DCFCE7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AccountBalanceWalletIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 16 }}>
                            {t('wallet_top_up') || 'Wallet Top Up'}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                            {t('select_user_amount') || 'Select user and enter amount'}
                        </Typography>
                    </Box>
                </Box>

                {/* Card Content */}
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {users && (
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#374151', mb: 1 }}>
                                {t('select_user')}
                            </Typography>
                            <UsersCombo
                                placeholder={t('search_users') || 'Search users...'}
                                users={users}
                                value={userCombo}
                                onChange={(event, newValue) => {
                                    setUserCombo(newValue);
                                }}
                            />
                        </Box>
                    )}

                    <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#374151', mb: 1 }}>
                            {t('amount')}
                        </Typography>
                        <TextField
                            type="number"
                            variant="outlined"
                            fullWidth
                            size="small"
                            placeholder="0.00"
                            className={`${classes.inputField} ${isRTL === 'rtl' ? classes.inputRtl : ''}`}
                            InputProps={{
                                startAdornment: settings?.symbol && (
                                    <Typography sx={{ color: '#6B7280', mr: 1 }}>
                                        {settings.symbol}
                                    </Typography>
                                ),
                            }}
                            value={amount}
                            onChange={event => {
                                const { value } = event.target;
                                setAmount(value === '' || value === null || value === undefined ? 0 : parseFloat(value));
                            }}
                        />
                    </Box>

                    {/* Selected User Preview */}
                    {userCombo && (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#F9FAFB',
                                border: '1px solid #E5E7EB',
                            }}
                        >
                            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 0.5 }}>
                                {t('selected_user') || 'Selected User'}
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                                {userCombo.firstName} {userCombo.lastName}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                                {settings.AllowCriticalEditsAdmin ? userCombo.email : 'Email hidden'}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        onClick={handleAddBalance}
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{
                            bgcolor: '#111827',
                            color: '#FFFFFF',
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#374151',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {t('add_to_wallet')}
                    </Button>
                </Box>
            </Box>

            <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
        </Box>
    );
}
